import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { MenuItemGroup, MenuMetadataForChannel, Menu, OrderType, MenuItemGroupType, PreConfiguredMeal, MealType, TortillaItem, Restaurant, CustomerProfile, MetadataTag, TopLevelMenuGroup, TagType } from '@/models';
import { slugify } from '@/helpers/formatting';
import { Routes } from '@/router';
import some from 'lodash/some';
import { getRewardsExclusiveTagText, isGroupBYO, isMenuItemRewardsExclusive } from '@/helpers';
import { getTopLevelMenuInteraction } from '@/services/analytics/platforms/adobe-analytics/helpers';
import { HomePageLoadEvent } from '@/services/analytics/events/home-page-load-event';
import { PreHideStyleID } from '@/services/adobe-target/adobe-target.service';
import { CountryCode } from '@/store/ui';


// BYO Top Level Menu ID is hardcoded for now to differentiate from other PCEs
// This is a quick and dirty solution to get BYOFM out the door. This should be
// removed once we have a proper solution in place.
const BYO_TOP_LEVEL_MENU_ID = "4dedd7da-3b33-4f63-acc4-ce1da5dfe81a";
@Component({
  name: 'CmgTopLevelMenu',
})
export default class CmgTopLevelMenu extends Vue {
  @Prop({ required: false, default: OrderType.Regular, type: Number })
  orderType!: OrderType;
  hasScrolledIntoView = false;
  isOrderStart = false;

  get menu(): Menu | null {
    return this.$appStore.state.menu.activeMenu;
  }

  get menuMetadata(): MenuMetadataForChannel | null {
    return this.$appStore.state.menu.metadata;
  }

  get preconfiguredMeals(): PreConfiguredMeal[] {
    return this.$appStore.state.menu.preConfiguredMeals;
  }

  get arrowRightImageUrl(): string {
    return this.$appStore.state.cms.images.icons.arrowRightGold.default;
  }

  get groups(): MenuItemGroup[] {
    if (!this.menu || !this.menuMetadata) {
      return [];
    }

    return this.$services.metadata.getValidMenuGroupsForMenu(this.menu, this.menuMetadata, true);
  }

  get countryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }

  get isByofmEnabledCountry(): boolean {
    return this.countryCode === CountryCode.US || this.countryCode === CountryCode.CA;
  }

  get filteredGroups(): MenuItemGroup[] {
    let filteredGroups = this.groups.filter(g => {
      return (this.isEnrolledInLoyalty || !isMenuItemRewardsExclusive(g.tags)) && this.shouldDisplayGroupIfBYO(g);
    });

    if (this.adobeTargetReorderedTopLevelMenu) {
      filteredGroups.sort(this.sortByAdobeTarget);
    }
    if (!this.$appStore.state.restaurant.selectedRestaurant) {
      filteredGroups = filteredGroups.filter(g => g.displayName != 'Drinks');
    }

    return filteredGroups;
  }

  get adobeTargetReorderedTopLevelMenu(): TopLevelMenuGroup[] | null {
   return this.$appStore.state.adobeTarget.reorderedTopLevelMenu; 
  }

  get selectedRestaurant(): Restaurant | null {
    return this.$appStore.state.restaurant.selectedRestaurant;
  }

  get isMenuLoading(): boolean {
    return this.$appStore.state.menu.isLoadingRestaurantMenu;
  }

  get customer(): CustomerProfile | null {
    return this.$appStore.state.customer.profile;
  }

  get isEnrolledInLoyalty() {
    return this.$services.loyalty.isEnrolledInLoyalty(this.customer);
  }

  mounted() {
    this.$services.adobeTarget.prehideContent(PreHideStyleID.topLevelMenuThumbnailImage, 5000);
    
    window.addEventListener('scroll', this.onScroll, true);

    if (!this.$appStore.state.ui.bag.visible && this.$route.name == 'meal-builder') {
      Vue.nextTick(() => this.$services.ui.nextFocus('mealBurrito'));
    }
  }

  destroyed() {
    window.removeEventListener('scroll', this.onScroll, true);
  }

  onScroll() {
    if (this.isTopLevelMenuVisible(this.$refs.topLevelMenu as Element)) {
      if (!this.hasScrolledIntoView && this.$route.hash != '#menu') {
        this.$services.analytics.send(new HomePageLoadEvent(true));
      }
      this.hasScrolledIntoView = true;
    }
  }

  isBYODisplay(menuItemType: MealType): boolean {
    return menuItemType == MealType.BuildYourOwn;
  }

  getGroupThumbnailUrl(group: MenuItemGroup) {
    const targetImageConent =  this.$appStore.state.adobeTarget.topLevelMenuImages.find(t => t.topLevelMenuTitle == group.displayName);

    return targetImageConent ? targetImageConent.thumbnailImageUrl : group.thumbnailImageUrl;
  }

  isTopLevelMenuVisible(element: Element): boolean {
    if (!element) {
      return false;
    }
    const boundingRect = element.getBoundingClientRect();
    return !(boundingRect.top + 240 > window.innerHeight || boundingRect.bottom - 240  < 0);
  }

  getRewardsText(tags: MetadataTag[]): string {
    return getRewardsExclusiveTagText(tags);
  }
  
  showRewardsExclusivePill(group: MenuItemGroup): boolean {
    return this.isEnrolledInLoyalty && isMenuItemRewardsExclusive(group.tags);
  }

  // For default items, we can use the item lookup map
  isDefaultTopLevelMenuItemAvailable(group: MenuItemGroup): boolean {
    const menuItems = this.$appStore.state.menu.menuItemLookupMap;

    if (!menuItems) {
      return false;
    }

    return some(group.items, item => {
      const availableItem = menuItems.get(item.menuItemId);
      return availableItem && availableItem.isItemAvailable;
    });
  }

  isItemGroupAvailable(group: MenuItemGroup): boolean {
    // If restaurant isn't selected or menu is loading, we need to make the menu
    // items appear to be available even if they may not. Otherwise the items
    // will appear unavailable temporarily
    if (this.isMenuLoading || !this.selectedRestaurant) {
      return true;
    }

    if (!group) {
      return false;
    }

    switch (group.type) {
      case MenuItemGroupType.Default:
        return this.isDefaultTopLevelMenuItemAvailable(group);
      case MenuItemGroupType.PreConfigured:
        return this.preconfiguredMeals && some(this.preconfiguredMeals, pce => pce.mealType == group.menuItemType);
      case MenuItemGroupType.Nested:
        // Check if nested Kids BYO or Kids Quesadilla is available
        return this.$services.menu.areTortillaItemsAvailable(TortillaItem.KidsByo) || this.$services.menu.areTortillaItemsAvailable(TortillaItem.KidsQuesadilla);
      case MenuItemGroupType.Quantity:
        return this.$services.menu.areTortillaItemsAvailable(TortillaItem.Tacos);
      default:
        return true;
    }
  }

  onItemGroupClick(group: MenuItemGroup, itemPosition: number) {
    if (!this.isItemGroupAvailable(group)) {
      return;
    }
    this.$services.googleAnalytics.sendProductClickEvent(group.displayName);
    this.$services.analytics.addLinkClickedEventToStore(getTopLevelMenuInteraction(itemPosition, group.displayName.toUpperCase()));

    const routeName = this.orderType == OrderType.Regular ? Routes.MealBuilder : Routes.GroupOrderMealBuilder;
    this.$router.push({ name: routeName, params: { groupName: slugify(group.displayName) } });
  }

  sortByAdobeTarget(item1: MenuItemGroup, item2: MenuItemGroup) { 
    const matchingItem1 = this.adobeTargetReorderedTopLevelMenu?.find(x => x.menuItemGroupId == item1.id);
    const matchingItem2 = this.adobeTargetReorderedTopLevelMenu?.find(x => x.menuItemGroupId == item2.id);

    if (matchingItem1 && matchingItem2) {
      return matchingItem1.sortOrder - matchingItem2.sortOrder;
    }
    
    return 0;
  }

  // IF PCE entree items are not available for the BYO group, return false
  private shouldDisplayGroupIfBYO(group: MenuItemGroup): boolean {
    if (isGroupBYO(group)) {
      return this.isByofmEnabledCountry;
    }
    return true;
  }

  hasPromoTag(group: MenuItemGroup) {
    return !!group.tags.find(t => t.type == TagType.Promo);
  }

  getPromoTagText(group: MenuItemGroup) {
    return group.tags.find(t => t.type == TagType.Promo)?.value || '';
  }

  hasTaglineTag(group: MenuItemGroup) {
    return !!group.tags.find(t => t.type == TagType.Tagline);
  }

  getTaglineTagText(group: MenuItemGroup) {
    return group.tags.find(t => t.type == TagType.Tagline)?.value || '';
  }
}
